import React, { useState, useEffect } from 'react';
import { 
    PlusCircle, Edit, Trash2, X, AlertTriangle, CheckCircle, Search, 
    User, Heart, Mail, Phone, Clock
} from 'lucide-react';

// Constante para la clave de localStorage
const STORAGE_KEY = 'clinicAppPacientesData';

// Estructura para el Contacto de Emergencia
const initialContacto = {
    nombre_contacto: '', 
    telefono_contacto: '', 
    parentesco_contacto: ''
};

// Datos de prueba iniciales (Solo si no hay datos en localStorage)
const defaultPacientesData = [
    { 
        id: 'p100', 
        nombre: 'Ana García', 
        edad: 45, 
        medico_asignado: 'Dr. Lisset', 
        telefono: '55-1234-5678',
        correo: 'ana.garcia@salud.com', 
        donador_organos: true,
        contacto_emergencia: { 
            nombre_contacto: 'Pedro García', 
            telefono_contacto: '55-1111-2222', 
            parentesco_contacto: 'Esposo'
        }
    },
    { 
        id: 'p101', 
        nombre: 'Roberto Pérez', 
        edad: 28, 
        medico_asignado: 'Dr. Javier', 
        telefono: '55-8765-4321',
        correo: 'roberto.perez@salud.com',
        donador_organos: false,
        contacto_emergencia: { 
            nombre_contacto: 'María López', 
            telefono_contacto: '55-9876-5432', 
            parentesco_contacto: 'Madre'
        }
    },
    { 
        id: 'p102', 
        nombre: 'Luisa Hernández', 
        edad: 62, 
        medico_asignado: 'Dra. Elena', 
        telefono: '55-3000-0001',
        correo: 'luisa.h@salud.com',
        donador_organos: true,
        contacto_emergencia: initialContacto
    },
    { 
        id: 'p103', 
        nombre: 'Miguel Torres', 
        edad: 19, 
        medico_asignado: 'Dr. Javier', 
        telefono: '55-4000-0002',
        correo: 'miguel.t@salud.com',
        donador_organos: false,
        contacto_emergencia: initialContacto
    }
];

// Función para cargar los datos desde localStorage
const loadPacientesFromLocalStorage = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const data = JSON.parse(stored);
            if (Array.isArray(data)) {
                return data;
            }
        }
    } catch (error) {
        console.error("Error al cargar datos de localStorage:", error);
    }
    // Si falla o no hay datos, retorna los datos por defecto
    return defaultPacientesData;
};


// Estructura base para un paciente nuevo o en edición
const initialPaciente = {
    id: null, 
    nombre: '', 
    edad: '', 
    medico_asignado: '',
    telefono: '',
    correo: '', 
    donador_organos: false,
    contacto_emergencia: initialContacto       
};


// 1. Componente de Notificación (Toast)
const Notification = ({ message, type, onClose }) => {
    if (!message) return null;

    const baseClasses = "fixed top-4 left-1/2 transform -translate-x-1/2 z-50 p-4 rounded-xl shadow-2xl flex items-center transition-all duration-300";
    let icon, colorClasses;

    switch (type) {
        case 'success':
            icon = <CheckCircle className="w-6 h-6 mr-3" />;
            colorClasses = "bg-green-600 text-white";
            break;
        case 'error':
            icon = <AlertTriangle className="w-6 h-6 mr-3" />;
            colorClasses = "bg-red-600 text-white";
            break;
        default:
            icon = <Clock className="w-6 h-6 mr-3" />;
            colorClasses = "bg-indigo-600 text-white";
    }

    return (
        <div className={`${baseClasses} ${colorClasses} animate-slideIn`}>
            {icon}
            <span className="font-semibold">{message}</span>
            <button onClick={onClose} className="ml-4 p-1 rounded-full hover:bg-white/20 transition">
                <X className="w-4 h-4" />
            </button>
        </div>
    );
};

// 2. Componente de Modal de Confirmación
const ConfirmationModal = ({ isOpen, onConfirm, onCancel, patientName, patientId }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-50 p-4 backdrop-blur-sm" aria-modal="true" role="dialog">
            <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 transform transition-all scale-100 duration-300">
                <div className="text-center">
                    <AlertTriangle className="mx-auto h-12 w-12 text-red-500" />
                    <h3 className="text-xl font-bold text-gray-800 mt-4">Confirmar Eliminación</h3>
                    <p className="text-sm text-gray-500 mt-2">
                        ¿Estás seguro de que quieres eliminar al paciente **{patientName}** (ID: {patientId})?
                        Esta acción no se puede deshacer.
                    </p>
                </div>
                <div className="mt-5 sm:mt-6 flex justify-end space-x-3">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition duration-150 font-medium"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-150 font-medium shadow-md"
                    >
                        Sí, Eliminar
                    </button>
                </div>
            </div>
        </div>
    );
};


export default function PacientesView() {
    // 1. Cargar datos del estado local, AHORA DESDE localStorage
    const [pacientes, setPacientes] = useState(loadPacientesFromLocalStorage);
    
    // ** NUEVO: Efecto para GUARDAR en localStorage cada vez que 'pacientes' cambie **
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(pacientes));
        } catch (error) {
            console.error("Error al guardar datos en localStorage:", error);
            // Si el almacenamiento falla (ej: cuota llena), se notifica al usuario si fuera necesario.
        }
    }, [pacientes]);

    // UI States
    const [searchTerm, setSearchTerm] = useState('');
    const [notification, setNotification] = useState({ message: '', type: '' });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [currentPaciente, setCurrentPaciente] = useState(initialPaciente);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving] = useState(false); // Mantener para UX del botón

    // --- Funciones de Utilidad y Notificación ---
    const showNotification = (msg, type = 'default') => {
        setNotification({ message: msg, type: type });
        setTimeout(() => setNotification({ message: '', type: '' }), 3000);
    };

    const closeNotification = () => {
        setNotification({ message: '', type: '' });
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setIsEditing(false);
        setCurrentPaciente(initialPaciente);
    };

    // --- Lógica de Búsqueda y Filtrado ---
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const filteredPacientes = pacientes.filter(paciente => {
        const term = searchTerm.toLowerCase();
        
        const matches = (
            paciente.nombre?.toLowerCase().includes(term) ||
            paciente.medico_asignado?.toLowerCase().includes(term) ||
            paciente.telefono?.includes(term) ||
            paciente.correo?.toLowerCase().includes(term) ||
            paciente.edad?.toString().includes(term) ||
            paciente.id?.toString().includes(term) ||
            paciente.contacto_emergencia?.nombre_contacto?.toLowerCase().includes(term) ||
            paciente.contacto_emergencia?.telefono_contacto?.includes(term) ||
            paciente.contacto_emergencia?.parentesco_contacto?.toLowerCase().includes(term)
        );
        return matches;
    });

    // --- Lógica del CRUD (Local State) ---

    // CREAR/EDITAR
    const handleCreate = () => {
        setIsEditing(false);
        setCurrentPaciente(initialPaciente);
        setIsModalOpen(true);
    };

    const handleEdit = (paciente) => {
        setIsEditing(true);
        // Usamos una copia para evitar mutar el estado original antes de guardar
        setCurrentPaciente({
            ...paciente,
            // Aseguramos que la edad sea un string para el input
            edad: paciente.edad?.toString() || '' 
        }); 
        setIsModalOpen(true);
    };

    // ELIMINAR (Abre la modal de confirmación)
    const handleDelete = (paciente) => {
        setCurrentPaciente(paciente);
        setIsDeleteModalOpen(true);
    };

    // Confirmación de ELIMINAR (Local State)
    const confirmDelete = () => {
        setPacientes(pacientes.filter(p => p.id !== currentPaciente.id));
        showNotification(`Paciente ${currentPaciente.nombre} eliminado permanentemente.`, 'success');
        setIsDeleteModalOpen(false);
        setCurrentPaciente(initialPaciente);
    };

    // Manejar cambios en el formulario (Maneja campos anidados)
    const handleFormChange = (e) => {
        const { name, value, type, checked, dataset } = e.target;
        
        if (dataset.group === 'emergencia') {
            setCurrentPaciente(prev => ({ 
                ...prev, 
                contacto_emergencia: { 
                    ...prev.contacto_emergencia, 
                    [name]: value 
                }
            }));
        } else {
            const newValue = type === 'checkbox' ? checked : value;
            setCurrentPaciente(prev => ({ ...prev, [name]: newValue }));
        }
    };

    // Guardar el formulario (CREATE o UPDATE en Local State)
    const handleSubmit = (e) => {
        e.preventDefault();

        const finalPaciente = { 
            ...currentPaciente,
            // Convertir la edad a número
            edad: Number(currentPaciente.edad), 
        };
        
        if (isEditing) {
            // UPDATE
            setPacientes(pacientes.map(p => p.id === finalPaciente.id 
                ? finalPaciente
                : p)
            );
            showNotification(`Paciente ${finalPaciente.nombre} modificado con éxito.`, 'success');
        } else {
            // CREATE
            // Generar un nuevo ID único basado en la marca de tiempo (más robusto que solo incremental)
            const newId = 'p' + Date.now();
            
            setPacientes([...pacientes, { ...finalPaciente, id: newId }]);
            showNotification(`Paciente ${finalPaciente.nombre} agregado con éxito.`, 'success');
        }

        closeModal();
    };


    return (
        <div className="p-4 md:p-8 bg-gray-50 min-h-screen font-sans">
            <style>
                {`
                @keyframes slideIn {
                    from { transform: translate(-50%, -100%); opacity: 0; }
                    to { transform: translate(-50%, 0); opacity: 1; }
                }
                .animate-slideIn {
                    animation: slideIn 0.5s ease-out forwards;
                }
                `}
            </style>

            {/* Notificación Visual Flotante */}
            <Notification 
                message={notification.message}
                type={notification.type}
                onClose={closeNotification}
            />

            <header className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center">
                <h1 className="text-3xl font-extrabold text-indigo-700 mb-4 md:mb-0">
                    <User className="inline-block w-8 h-8 mr-3 text-indigo-500" />
                    Gestión Clínica de Pacientes
                </h1>
                <button
                    onClick={handleCreate} 
                    className="flex items-center px-6 py-2 bg-indigo-600 text-white font-medium rounded-lg shadow-lg hover:bg-indigo-700 transition duration-150 transform hover:scale-[1.02] active:scale-95"
                >
                    <PlusCircle className="w-5 h-5 mr-2" />
                    Agregar Paciente
                </button>
            </header>
            
            {/* BARRA DE BÚSQUEDA */}
            <div className="mb-6 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                    type="text"
                    placeholder="Buscar por nombre, ID, médico, teléfono, correo o contacto de emergencia..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="w-full p-3 pl-10 border-2 border-gray-300 rounded-xl shadow-inner focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition duration-150"
                />
            </div>

            <h2 className="text-xl font-semibold text-gray-700 mb-4">
                Listado Detallado de Pacientes ({filteredPacientes.length})
            </h2>

            {/* Tabla de Pacientes */}
            <div className="overflow-x-auto bg-white shadow-2xl rounded-xl">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-indigo-100">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-bold text-indigo-800 uppercase tracking-wider">ID</th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-indigo-800 uppercase tracking-wider">Nombre</th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-indigo-800 uppercase tracking-wider">Edad</th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-indigo-800 uppercase tracking-wider">Correo</th> 
                            <th className="px-6 py-3 text-center text-xs font-bold text-indigo-800 uppercase tracking-wider">Donador</th> 
                            <th className="px-6 py-3 text-left text-xs font-bold text-indigo-800 uppercase tracking-wider">Médico Asignado</th>
                            <th className="px-6 py-3 text-center text-xs font-bold text-indigo-800 uppercase tracking-wider">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                        {filteredPacientes.length > 0 ? (
                            filteredPacientes.map((paciente) => (
                                <tr key={paciente.id} className="hover:bg-indigo-50/20 transition duration-100">
                                    <td className="px-6 py-4 whitespace-nowrap text-xs font-semibold text-gray-900">{paciente.id}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 font-medium">{paciente.nombre}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{paciente.edad}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 flex items-center">
                                        <Mail className="w-4 h-4 mr-1 text-indigo-400"/> {paciente.correo}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center"> 
                                        <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${
                                            paciente.donador_organos 
                                                ? 'bg-red-100 text-red-800' 
                                                : 'bg-green-100 text-green-800'
                                        }`}>
                                            <Heart className={`w-3 h-3 mr-1 ${paciente.donador_organos ? 'text-red-500' : 'text-green-500'}`} fill="currentColor"/>
                                            {paciente.donador_organos ? 'Sí' : 'No'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{paciente.medico_asignado}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                        <div className="flex justify-center space-x-2">
                                            <button
                                                onClick={() => handleEdit(paciente)} 
                                                className="p-2 text-indigo-600 hover:text-indigo-900 rounded-full hover:bg-indigo-100 transition duration-150"
                                                title="Editar"
                                            >
                                                <Edit className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(paciente)} 
                                                className="p-2 text-red-600 hover:text-red-900 rounded-full hover:bg-red-100 transition duration-150"
                                                title="Eliminar"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="7" className="px-6 py-8 text-center text-gray-500 text-lg">
                                    No se encontraron pacientes que coincidan con la búsqueda.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* ==================================================================== */}
            {/* MODAL de CREAR/EDITAR (FORMULARIO) */}
            {/* ==================================================================== */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl p-6 transform transition-all duration-300 scale-100 max-h-[90vh] overflow-y-auto">
                        <h3 className="text-2xl font-bold mb-6 text-indigo-700 border-b pb-2 flex items-center">
                            {isEditing ? <Edit className="w-6 h-6 mr-2"/> : <PlusCircle className="w-6 h-6 mr-2"/>}
                            {isEditing ? 'Editar Paciente' : 'Agregar Nuevo Paciente'}
                        </h3>
                        
                        <form onSubmit={handleSubmit}>
                            
                            {/* SECCIÓN 1: Datos Personales Básicos */}
                            <h4 className="text-lg font-semibold text-gray-600 mb-3 border-l-4 border-indigo-500 pl-3">Información General</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                
                                <div className="col-span-1 md:col-span-2">
                                    <label className="block text-gray-700 text-sm font-semibold mb-2">Nombre Completo:</label>
                                    <input
                                        type="text"
                                        name="nombre"
                                        required
                                        value={currentPaciente.nombre || ''}
                                        onChange={handleFormChange}
                                        className="border border-gray-300 rounded-lg w-full py-2 px-4 text-gray-800 focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Nombre completo del paciente"
                                    />
                                </div>

                                <div>
                                    <label className="block text-gray-700 text-sm font-semibold mb-2">Edad:</label>
                                    <input
                                        type="number"
                                        name="edad"
                                        required
                                        value={currentPaciente.edad || ''}
                                        onChange={handleFormChange}
                                        className="border border-gray-300 rounded-lg w-full py-2 px-4 text-gray-800 focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Ej: 30"
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-gray-700 text-sm font-semibold mb-2 flex items-center"><Phone className="w-4 h-4 mr-1"/> Teléfono:</label>
                                    <input
                                        type="tel"
                                        name="telefono"
                                        required
                                        value={currentPaciente.telefono || ''}
                                        onChange={handleFormChange}
                                        className="border border-gray-300 rounded-lg w-full py-2 px-4 text-gray-800 focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="55-1234-5678"
                                    />
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                <div>
                                    <label className="block text-gray-700 text-sm font-semibold mb-2 flex items-center"><Mail className="w-4 h-4 mr-1"/> Correo del Paciente:</label>
                                    <input
                                        type="email"
                                        name="correo"
                                        value={currentPaciente.correo || ''}
                                        onChange={handleFormChange}
                                        className="border border-gray-300 rounded-lg w-full py-2 px-4 text-gray-800 focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="correo@ejemplo.com"
                                    />
                                </div>

                                <div>
                                    <label className="block text-gray-700 text-sm font-semibold mb-2">Médico Asignado:</label>
                                    <input
                                        type="text"
                                        name="medico_asignado"
                                        required
                                        value={currentPaciente.medico_asignado || ''}
                                        onChange={handleFormChange}
                                        className="border border-gray-300 rounded-lg w-full py-2 px-4 text-gray-800 focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Nombre del médico (Ej: Dr. Javier)"
                                    />
                                </div>
                            </div>

                            {/* Campo Donador de Órganos (CHECKBOX) */}
                            <div className="mb-6 bg-red-50 p-4 rounded-lg border border-red-200">
                                <label className="flex items-center space-x-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="donador_organos"
                                        checked={currentPaciente.donador_organos || false}
                                        onChange={handleFormChange}
                                        className="h-5 w-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                                    />
                                    <span className="text-sm font-semibold text-red-700 flex items-center">
                                        <Heart className="w-4 h-4 mr-1" fill="currentColor"/>
                                        ¿Es donador de órganos? (Consentimiento explícito)
                                    </span>
                                </label>
                            </div>
                            
                            {/* SECCIÓN 2: Contacto de Emergencia */}
                            <h4 className="text-lg font-semibold text-gray-600 mb-3 border-l-4 border-indigo-500 pl-3">Contactos de Emergencia</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                                
                                {/* Nombre Contacto */}
                                <div>
                                    <label className="block text-gray-700 text-sm font-semibold mb-2">Nombre Contacto:</label>
                                    <input
                                        type="text"
                                        name="nombre_contacto"
                                        data-group="emergencia"
                                        value={currentPaciente.contacto_emergencia?.nombre_contacto || ''}
                                        onChange={handleFormChange}
                                        className="border border-gray-300 rounded-lg w-full py-2 px-4 text-gray-800 focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Nombre del contacto"
                                    />
                                </div>
                                
                                {/* Teléfono Contacto */}
                                <div>
                                    <label className="block text-gray-700 text-sm font-semibold mb-2">Teléfono Contacto:</label>
                                    <input
                                        type="tel"
                                        name="telefono_contacto"
                                        data-group="emergencia"
                                        value={currentPaciente.contacto_emergencia?.telefono_contacto || ''}
                                        onChange={handleFormChange}
                                        className="border border-gray-300 rounded-lg w-full py-2 px-4 text-gray-800 focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="55-9999-8888"
                                    />
                                </div>

                                {/* Parentesco Contacto */}
                                <div>
                                    <label className="block text-gray-700 text-sm font-semibold mb-2">Parentesco:</label>
                                    <input
                                        type="text"
                                        name="parentesco_contacto"
                                        data-group="emergencia"
                                        value={currentPaciente.contacto_emergencia?.parentesco_contacto || ''}
                                        onChange={handleFormChange}
                                        className="border border-gray-300 rounded-lg w-full py-2 px-4 text-gray-800 focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Ej: Hermano, Vecino, etc."
                                    />
                                </div>
                            </div>


                            {/* Botones de acción */}
                            <div className="flex justify-end space-x-4 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="bg-gray-300 text-gray-800 font-semibold py-2 px-6 rounded-lg hover:bg-gray-400 transition duration-150 shadow-md"
                                    disabled={isSaving}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="bg-indigo-600 text-white font-semibold py-2 px-6 rounded-lg hover:bg-indigo-700 transition duration-150 shadow-md flex items-center"
                                    disabled={isSaving}
                                >
                                    {isSaving && <div className="animate-spin inline-block w-4 h-4 border-2 border-t-2 border-white border-opacity-75 rounded-full mr-2"></div>}
                                    {isEditing ? 'Guardar Cambios' : 'Guardar Paciente'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL de CONFIRMACIÓN de ELIMINACIÓN */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                patientName={currentPaciente.nombre || 'N/A'}
                patientId={currentPaciente.id || 'N/A'}
                onConfirm={confirmDelete}
                onCancel={() => setIsDeleteModalOpen(false)}
            />
        </div>
    );
}